import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Heading,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Switch,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Textarea,
    useToast,
    Spinner,
    Center,
    VStack,
    HStack,
    Tag
} from '@chakra-ui/react';
import {
    FiPlus,
    FiSearch,
    FiEdit,

    FiMoreVertical,
    FiCheckCircle,
    FiClock,
    FiZap,
    FiSend,

    FiPause,

    FiGitBranch,
} from 'react-icons/fi';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';
import Header from '../../components/Header';
import { useNavigate } from 'react-router-dom';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { LuTrendingUp } from 'react-icons/lu';

export default function FlowList() {
    const toast = useToast();
    const [flows, setFlows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(10);

    // Delete dialog
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [flowToDelete, setFlowToDelete] = useState(null);
    const cancelRef = React.useRef();

    // Manual trigger dialog
    const { isOpen: isTriggerOpen, onOpen: onTriggerOpen, onClose: onTriggerClose } = useDisclosure();
    const [flowToTrigger, setFlowToTrigger] = useState(null);
    const [triggerMethod, setTriggerMethod] = useState('all_contacts');
    const [contactsInput, setContactsInput] = useState('');
    const [triggerNote, setTriggerNote] = useState('');
    const navigate = useNavigate();


    const fetchFlows = async () => {
        setLoading(true);
        try {
            const params = {
                page: page,
                limit: limit,
            };

            if (searchTerm) {
                params.trigger_name = searchTerm;
            }

            if (statusFilter && statusFilter !== 'all') {
                params.status = statusFilter;
            }

            const response = await API.get(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/list`,
                {
                    params,
                    headers: getApiHeaders()
                }
            );

            setFlows(response.data.flows || []);
            setTotalPages(response.data.totalPages || 1);
        } catch (error) {
            console.error('Error fetching flows:', error);
            toast({
                title: 'Error fetching flows',
                description: error.response?.data?.message || error.message || 'Failed to fetch flows',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            setFlows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchTerm, statusFilter]);

    const handleToggleStatus = async (flowId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
            const response = await API.post(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/update-status`,
                { status: newStatus, flow_id: flowId },
                { headers: getApiHeaders() }
            );

            toast({
                title: 'Flow updated',
                description: `Flow ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
                status: 'success',
                duration: 2000,
                isClosable: true,
            });

            fetchFlows();
        } catch (error) {
            console.error('Error updating flow:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || error.message || 'Failed to update flow',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleDeleteFlow = async () => {
        if (!flowToDelete) return;

        try {
            const response = await API.delete(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/delete?id=${flowToDelete._id}`,
                { headers: getApiHeaders() }
            );

            toast({
                title: 'Flow deleted',
                description: 'Flow has been deleted successfully',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });

            onDeleteClose();
            setFlowToDelete(null);
            fetchFlows();
        } catch (error) {
            console.error('Error deleting flow:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || error.message || 'Failed to delete flow',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleDuplicateFlow = async (flow) => {
        try {
            const response = await API.post(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/duplicate?id=${flow._id}`,
                {},
                { headers: getApiHeaders() }
            );

            toast({
                title: 'Flow duplicated',
                description: 'Flow has been duplicated successfully',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });

            fetchFlows();
        } catch (error) {
            console.error('Error duplicating flow:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || error.message || 'Failed to duplicate flow',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleManualTrigger = (flow) => {
        setFlowToTrigger(flow);
        onTriggerOpen();
    };

    const handleTriggerFlow = () => {
        if (!flowToTrigger) return;

        let contactCount = 0;
        if (triggerMethod === 'all_contacts') {
            contactCount = flowToTrigger.total_contact || 0;
        } else if (triggerMethod === 'specific_contacts') {
            const contactIds = contactsInput.split(/[,\n]/).filter(id => id.trim());
            contactCount = contactIds.length;
        }

        toast({
            title: 'Flow triggered',
            description: `Flow "${flowToTrigger.name}" triggered successfully for ${contactCount.toLocaleString()} contact(s)!`,
            status: 'success',
            duration: 3000,
            isClosable: true,
        });

        onTriggerClose();
        setFlowToTrigger(null);
        setTriggerMethod('all_contacts');
        setContactsInput('');
        setTriggerNote('');
    };

    const handleEditFlow = (flowId) => {
        // For demonstration - replace with your routing logic
        navigate(`/marketing/flow/${flowId}`);

    };

    const handleCreateFlow = () => {
        // For demonstration - replace with your routing logic
        navigate('/marketing/flow-create');

    };

    const stats = {
        total: flows.length,
        active: flows.filter(f => f.status === 'active').length,
        inactive: flows.filter(f => f.status === 'inactive').length,
        draft: flows.filter(f => f.status === 'draft').length,
    };

    const handleViewExecution = (flowId) => {
        navigate(`/marketing/flow-execution/${flowId}`);
    }
    return (
        <>
            <Header />
            <BoxWrapper>
                <VStack spacing={6} align="stretch" p={1}>
                    {/* Header */}
                    <Flex justify="space-between" align="center">
                        <Box>
                            <Heading size="lg" display="flex" alignItems="center" gap={2}>
                                <Box as={FiGitBranch} color="var(--brand-primary)" />
                                xFlow Manager
                            </Heading>
                            <Text color="gray.600" mt={1}>
                                Create and manage automated customer journey flows
                            </Text>
                        </Box>
                        <Button
                            leftIcon={<FiPlus />}
                            colorScheme="blue"
                            bg="var(--brand-primary)"
                            _hover={{ bg: 'var(--brand-primary-hover)' }}
                            onClick={handleCreateFlow}
                            size={"sm"}
                        >
                            Create Flow
                        </Button>
                    </Flex>

                    {/* Stats Cards */}
                    <Flex gap={4} wrap="wrap">
                        <Box className='box-style-card' flex="1" minW="200px">
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" fontSize="sm" mb={1}>Total Flows</Text>
                                        <Text fontSize="2xl" fontWeight="bold">{stats.total}</Text>
                                    </Box>
                                    <Box as={FiGitBranch} fontSize="32px" color="gray.400" />
                                </Flex>
                            </Box>
                        </Box>
                        <Box className='box-style-card' flex="1" minW="200px">
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" fontSize="sm" mb={1}>Active Flows</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="green.600">{stats.active}</Text>
                                    </Box>
                                    <Box as={FiCheckCircle} fontSize="32px" color="green.600" />
                                </Flex>
                            </Box>
                        </Box>
                        <Box className='box-style-card' flex="1" minW="200px">
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" fontSize="sm" mb={1}>Inactive Flows</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="orange.600">{stats.inactive}</Text>
                                    </Box>
                                    <Box as={FiPause} fontSize="32px" color="orange.600" />
                                </Flex>
                            </Box>
                        </Box>
                        <Box className='box-style-card' flex="1" minW="200px">
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" fontSize="sm" mb={1}>Draft Flows</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="gray.600">{stats.draft}</Text>
                                    </Box>
                                    <Box as={FiClock} fontSize="32px" color="gray.600" />
                                </Flex>
                            </Box>
                        </Box>
                    </Flex>

                    {/* Filters */}
                    <Box className='box-style-card'>
                        <Box>
                            <Flex gap={4} wrap="wrap">
                                <InputGroup flex="1" minW="250px">
                                    <InputLeftElement pointerEvents="none">
                                        <FiSearch color="gray" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search flows by trigger name..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setPage(1);
                                        }}
                                    />
                                </InputGroup>
                                <Select
                                    w="180px"
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="draft">Draft</option>
                                </Select>
                            </Flex>
                        </Box>
                    </Box>

                    {/* Flows Table */}
                    <Box className='box-style-card'>
                        <Box p={0}>
                            {loading ? (
                                <Center py={12}>
                                    <Spinner size="xl" color="var(--brand-primary)" />
                                </Center>
                            ) : (
                                <Box overflowX="auto">
                                    <Table variant="simple" tableLayout="fixed">
                                        <Thead>
                                            <Tr>
                                                <Th w="15%" textAlign="left">Flow Name</Th>
                                                <Th w="12%" textAlign="left">Trigger Event</Th>
                                                <Th w="8%" textAlign="left">Status</Th>
                                                <Th w="10%" textAlign="left">Total Contacts</Th>
                                                <Th w="10%" textAlign="left">Active Journeys</Th>
                                                <Th w="10%" textAlign="left">Conversion Rate</Th>
                                                <Th w="12%" textAlign="left">Last Updated</Th>
                                                <Th w="6%" textAlign="left">Actions</Th>
                                            </Tr>
                                        </Thead>

                                        <Tbody>
                                            {flows.length === 0 ? (
                                                <Tr>
                                                    <Td colSpan={8} textAlign="left">
                                                        <Center py={12}>
                                                            <VStack spacing={3}>
                                                                <Box as={FiGitBranch} fontSize="48px" color="gray.300" />
                                                                <Box>
                                                                    <Text color="gray.600">No flows found</Text>
                                                                    <Text color="gray.500" fontSize="sm" mt={1}>
                                                                        {searchTerm || statusFilter !== 'all'
                                                                            ? 'Try adjusting your filters'
                                                                            : 'Create your first flow to get started'}
                                                                    </Text>
                                                                </Box>
                                                            </VStack>
                                                        </Center>
                                                    </Td>
                                                </Tr>
                                            ) : (
                                                flows.map((flow) => (
                                                    <Tr key={flow._id}>
                                                        {/* Flow Name */}
                                                        <Td textAlign="left">
                                                            <Box maxW="100%">
                                                                <Text fontWeight="medium" noOfLines={1}>
                                                                    {flow.name}
                                                                </Text>
                                                                <Text fontSize="xs" color="gray.600" noOfLines={2}>
                                                                    {flow.description}
                                                                </Text>
                                                            </Box>
                                                        </Td>

                                                        {/* Trigger */}
                                                        <Td textAlign="left">
                                                            <Tag
                                                                size="sm"
                                                                border="1px solid #0000001a"
                                                                bg="transparent"
                                                                whiteSpace="nowrap"
                                                            >
                                                                {flow.trigger_event}
                                                            </Tag>
                                                        </Td>

                                                        {/* Status */}
                                                        <Td textAlign="left">
                                                            <Switch
                                                                colorScheme="blue"
                                                                isChecked={flow.status === 'active'}
                                                                onChange={() => handleToggleStatus(flow._id, flow.status)}
                                                            />
                                                        </Td>

                                                        {/* Total Contacts */}
                                                        <Td textAlign="left">
                                                            {(flow.total_contact || 0).toLocaleString()}
                                                        </Td>

                                                        {/* Active Journeys */}
                                                        <Td textAlign="left">
                                                            {flow.active_journeys > 0 ? (
                                                                <Tag
                                                                    size="sm"
                                                                    color="var(--brand-primary-hover)"
                                                                    bg="var(--brand-tint)"
                                                                    border="1px solid var(--brand-tint-strong)"
                                                                >
                                                                    {flow.active_journeys}
                                                                </Tag>
                                                            ) : (
                                                                <Text color="gray.500">-</Text>
                                                            )}
                                                        </Td>

                                                        {/* Conversion */}
                                                        <Td textAlign="left">
                                                            {flow.conversion_rate ? (
                                                                <Text
                                                                    fontWeight="medium"
                                                                    color={
                                                                        parseFloat(flow.conversion_rate) >= 30
                                                                            ? 'green.600'
                                                                            : parseFloat(flow.conversion_rate) >= 20
                                                                                ? 'orange.600'
                                                                                : 'red.600'
                                                                    }
                                                                >
                                                                    {flow.conversion_rate}
                                                                </Text>
                                                            ) : (
                                                                <Text color="gray.500">-</Text>
                                                            )}
                                                        </Td>

                                                        {/* Last Updated */}
                                                        <Td textAlign="left">
                                                            <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
                                                                {new Date(flow.updated_at).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })}
                                                            </Text>
                                                        </Td>

                                                        {/* Actions */}
                                                        <Td textAlign="left">
                                                            <Menu>
                                                                <MenuButton
                                                                    as={IconButton}
                                                                    icon={<FiMoreVertical />}
                                                                    variant="ghost"
                                                                    size="sm"
                                                                />
                                                                <MenuList>
                                                                    <MenuItem
                                                                        icon={<FiEdit />}
                                                                        onClick={() => handleEditFlow(flow._id)}
                                                                    >
                                                                        Edit Flow
                                                                    </MenuItem>
                                                                    <MenuItem
                                                                        icon={<LuTrendingUp />}
                                                                        onClick={() => handleViewExecution(flow._id)}
                                                                    >
                                                                        View Executions
                                                                    </MenuItem>
                                                                </MenuList>
                                                            </Menu>
                                                        </Td>
                                                    </Tr>
                                                ))
                                            )}
                                        </Tbody>
                                    </Table>


                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Flex justify="center" gap={2}>
                            <Button
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                isDisabled={page === 1}
                            >
                                Previous
                            </Button>
                            <Text alignSelf="center" px={4}>
                                Page {page} of {totalPages}
                            </Text>
                            <Button
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                isDisabled={page === totalPages}
                            >
                                Next
                            </Button>
                        </Flex>
                    )}
                </VStack>
            </BoxWrapper>
            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Flow
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete this flow? This action cannot be undone.
                            All active journeys will be stopped immediately.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteFlow} ml={3}>
                                Delete Flow
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Manual Trigger Dialog */}
            <Modal isOpen={isTriggerOpen} onClose={onTriggerClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader display="flex" alignItems="center" gap={2}>
                        <Box as={FiZap} color="var(--brand-primary)" />
                        Manually Start Flow
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Text color="gray.600" fontSize="sm">
                                Start "{flowToTrigger?.name}" manually for specific or all contacts
                            </Text>

                            <FormControl>
                                <FormLabel fontSize="sm">Trigger Method</FormLabel>
                                <Select
                                    value={triggerMethod}
                                    onChange={(e) => setTriggerMethod(e.target.value)}
                                >
                                    <option value="all_contacts">
                                        All Contacts ({(flowToTrigger?.total_contact || 0).toLocaleString()})
                                    </option>
                                    <option value="specific_contacts">
                                        Specific Contacts
                                    </option>
                                </Select>
                            </FormControl>

                            {triggerMethod === 'specific_contacts' && (
                                <FormControl>
                                    <FormLabel fontSize="sm">Contact IDs or Phone Numbers</FormLabel>
                                    <Textarea
                                        value={contactsInput}
                                        onChange={(e) => setContactsInput(e.target.value)}
                                        placeholder="Enter contact IDs or phone numbers (one per line or comma-separated)&#10;Example:&#10;+919876543210, +919876543211&#10;or&#10;CUST001, CUST002, CUST003"
                                        minH="100px"
                                    />
                                    <Text fontSize="xs" color="gray.600" mt={1.5}>
                                        Separate multiple contacts with commas or line breaks
                                    </Text>
                                </FormControl>
                            )}

                            <FormControl>
                                <FormLabel fontSize="sm">Notes (Optional)</FormLabel>
                                <Textarea
                                    value={triggerNote}
                                    onChange={(e) => setTriggerNote(e.target.value)}
                                    placeholder="Add any notes about this manual trigger (e.g., campaign name, reason for triggering)..."
                                />
                            </FormControl>

                            {/* Summary Box */}
                            <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md" p={4}>
                                <HStack align="start" spacing={3}>
                                    <Box as={FiZap} color="var(--brand-primary)" mt={0.5} flexShrink={0} />
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold" mb={2}>
                                            Summary:
                                        </Text>
                                        <VStack align="start" spacing={1} fontSize="xs" color="gray.600">
                                            <Text>Flow: {flowToTrigger?.name}</Text>
                                            <Text>Method: {triggerMethod === 'all_contacts' ? 'All Contacts' : 'Specific Contacts'}</Text>
                                            {triggerMethod === 'all_contacts' && (
                                                <Text>Total Contacts: {(flowToTrigger?.total_contact || 0).toLocaleString()}</Text>
                                            )}
                                            {triggerMethod === 'specific_contacts' && (
                                                <Text>Selected Contacts: {contactsInput.split(/[,\n]/).filter(id => id.trim()).length}</Text>
                                            )}
                                        </VStack>
                                    </Box>
                                </HStack>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onTriggerClose}>
                            Cancel
                        </Button>
                        <Button
                            leftIcon={<FiSend />}
                            colorScheme="blue"
                            bg="var(--brand-primary)"
                            _hover={{ bg: 'var(--brand-primary-hover)' }}
                            onClick={handleTriggerFlow}
                            isDisabled={triggerMethod === 'specific_contacts' && !contactsInput.trim()}
                        >
                            Start Flow Now
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}